import React from 'react';
import styled from 'styled-components';
import Tooltip from '@splunk/react-ui/Tooltip';
import ButtonSimple from '@splunk/react-ui/ButtonSimple';
import { _ } from '@splunk/ui-utils/i18n';
import { pickVariant, variables } from '@splunk/themes';

interface WrapperProps {
    onClick?: React.MouseEventHandler;
    hasVisibilityToggle: boolean;
    children: React.ReactNode;
}

const NameWrapper = styled.div<WrapperProps>`
    flex: 0 1 auto;
    min-width: 0;
    display: flex;
    align-items: center;
    border: 2px solid transparent;
`;

// SimpleButton gives more control over the label style, but is not documented by SUI
const StyledButton = styled(ButtonSimple)`
    flex: 0 1 auto;
    min-width: 0;
    justify-content: flex-start;
    padding-inline-start: ${variables.spacingSmall};
`;

const Wrapper = ({ onClick, children, hasVisibilityToggle }: WrapperProps) => {
    return onClick ? (
        <StyledButton
            data-test="item-name-wrapper"
            onClick={onClick}
            appearance="subtle"
        >
            {children}
        </StyledButton>
    ) : (
        <NameWrapper
            data-test="item-name-wrapper"
            hasVisibilityToggle={hasVisibilityToggle}
        >
            {children}
        </NameWrapper>
    );
};

interface RowProps {
    isUsed: boolean;
}

const Row = styled.div<RowProps>`
    width: 100%;
    min-width: 0;
    display: flex;
    flex-direction: row;
    align-items: center;
    color: ${pickVariant<RowProps>('isUsed', {
        true: variables.contentColorDefault,
        false: variables.contentColorMuted,
    })};
`;

const Name = styled.div.attrs(() => ({
    'data-test': 'item-name',
}))`
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const unusedText = _('(Unused)');
const Unused = styled.div`
    margin-left: 5px;
`;

const NameText = ({ name, isUsed }: { name: string; isUsed: boolean }) => (
    <Row isUsed={isUsed}>
        <Name>{name}</Name>
        {!isUsed && <Unused>{unusedText}</Unused>}
    </Row>
);

export interface ItemNameProps {
    name: string;
    onItemClick?: React.MouseEventHandler;
    isUsed?: boolean;
    hasVisibilityToggle?: boolean;
}

export const ItemName = ({
    name,
    onItemClick,
    isUsed = true,
    hasVisibilityToggle = false,
}: ItemNameProps) => {
    const textOverflow = name.length > 15;

    return (
        <Wrapper
            onClick={onItemClick}
            hasVisibilityToggle={hasVisibilityToggle}
        >
            <Tooltip
                data-test-disabled={!textOverflow}
                openDelay={250}
                closeDelay={100}
                // Tooltip won't show if content is falsy
                // we don't need a tooltip if not overflowing
                content={textOverflow ? name : false}
                inline={false}
            >
                <NameText name={name} isUsed={isUsed} />
            </Tooltip>
        </Wrapper>
    );
};
